import {FontAwesomeIcon} from "@fortawesome/react-fontawesome";
import {
  DocumentCardChrome,
  DocumentTile,
  DocumentTileFooterBadge,
  getFileTypeBadge,
} from "@mytour/documents";
import {downloadService} from "@mytour/platform";
import type {JardinierReportDocument} from "@mytour/shared";
import dayjs from "dayjs";
import "dayjs/locale/fr";
import {getEmailStatusBadgeState} from "@mytour/shared";
import {useEffect, useState} from "react";
import {toast} from "react-toastify";
import {useCustomer} from "../../client/query/customerQueries";
import {JardinierReportResendModal} from "../../patient/report/jardinier/actions/JardinierReportResendModal";
import {jardinierReportApiService} from "../../patient/report/jardinier/query/jardinierReportApi.service";
import {useJardinierReport} from "../../patient/report/jardinier/query/jardinierReportQueries";
import "./AttestationCard.css";

dayjs.locale("fr");

type Props = {
  doc: JardinierReportDocument;
  customerId: string;
};

const PDF_BADGE = getFileTypeBadge("application/pdf");

/**
 * Tuile de compte rendu jardinier dans la section Documents (#1278) — miroir d'`AttestationCard`
 * (#1229) : même tuile PDF, mêmes badges de statut d'envoi (#1270/#1292). Le clic sur la tuile
 * route vers le renvoi quand un badge de statut est affiché (non envoyé/échec), et télécharge le
 * PDF déjà stocké sinon.
 */
export function JardinierReportCard({doc, customerId}: Props) {
  const [isDownloading, setIsDownloading] = useState(false);
  const [isResendModalOpen, setIsResendModalOpen] = useState(false);
  const badgeState = getEmailStatusBadgeState(doc.emailStatus);
  const {jardinierReport, isJardinierReportLoading} = useJardinierReport(
    doc.eventId,
    isResendModalOpen
  );
  const {customer, isCustomerLoading} = useCustomer(customerId, isResendModalOpen);
  const isResendDataLoading = isResendModalOpen && (isJardinierReportLoading || isCustomerLoading);

  /**
   * Sans ce garde-fou, un badge dont le compte rendu/client ne se charge jamais (échec réseau,
   * ligne introuvable) reste cliqué mais silencieux pour toujours — la modale ne s'ouvre pas.
   */
  useEffect(() => {
    if (isResendModalOpen && !isResendDataLoading && (!jardinierReport || !customer)) {
      toast.error("Impossible d'ouvrir le renvoi du compte rendu.");
      setIsResendModalOpen(false);
    }
  }, [isResendModalOpen, isResendDataLoading, jardinierReport, customer]);

  return (
    <DocumentCardChrome>
      <div
        role="button"
        tabIndex={0}
        onClick={badgeState ? handleOpenResend : handleClick}
        onKeyDown={handleKeyDown}
        style={{cursor: isDownloading ? "wait" : "pointer"}}
      >
        <DocumentTile
          thumb={{kind: "badge", label: PDF_BADGE.label, color: PDF_BADGE.color}}
          title="Compte rendu d'intervention"
          subtitle={`Rdv du ${dayjs(doc.eventDate).format("DD/MM")}`}
          footer={<DocumentTileFooterBadge label="Compte rendu" />}
          actions={
            badgeState && (
              <span
                role="button"
                tabIndex={0}
                title={
                  badgeState === "failed"
                    ? "Échec d'envoi, cliquer pour corriger"
                    : "Non envoyé, cliquer pour envoyer"
                }
                className={`attestation-status-badge ${badgeState}`}
                onClick={handleBadgeClick}
                onKeyDown={handleBadgeKeyDown}
              >
                {isResendDataLoading ? (
                  <FontAwesomeIcon icon="circle-notch" spin />
                ) : badgeState === "failed" ? (
                  "!"
                ) : (
                  <FontAwesomeIcon icon="envelope" />
                )}
              </span>
            )
          }
        />
      </div>
      {isResendModalOpen && jardinierReport && customer && (
        <JardinierReportResendModal
          eventId={doc.eventId}
          report={jardinierReport}
          customer={customer}
          isFailure={badgeState === "failed"}
          onClose={() => setIsResendModalOpen(false)}
        />
      )}
    </DocumentCardChrome>
  );

  function handleKeyDown(event: React.KeyboardEvent<HTMLDivElement>) {
    if (event.key === "Enter" || event.key === " ") {
      event.preventDefault();
      if (badgeState) {
        handleOpenResend();
      } else {
        handleClick();
      }
    }
  }

  function handleOpenResend() {
    setIsResendModalOpen(true);
  }

  function handleBadgeClick(event: React.MouseEvent) {
    event.stopPropagation();
    handleOpenResend();
  }

  function handleBadgeKeyDown(event: React.KeyboardEvent) {
    if (event.key === "Enter" || event.key === " ") {
      event.preventDefault();
      event.stopPropagation();
      handleOpenResend();
    }
  }

  async function handleClick() {
    if (isDownloading) {
      return;
    }
    try {
      setIsDownloading(true);
      const blob = await jardinierReportApiService.downloadFile(doc.eventId);
      await downloadService.download(blob, doc.fileName);
    } catch {
      toast.error("Impossible d'ouvrir le compte rendu.");
    } finally {
      setIsDownloading(false);
    }
  }
}
